import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Card, Button, Alert, Spinner, Form, Table, Badge } from "react-bootstrap";
import { FaSearch, FaUserShield, FaUserMinus, FaHistory } from "react-icons/fa";

import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import OrientationPrompt from "../components/OrientationPrompt";

import {
  whoami,
  searchUsersByEmail,
  getAlpacaStaffList,
  getAlpacaStaffHistory,
  grantAlpacaStaff,
  revokeAlpacaStaff,
} from "../services/APIClient";
import { isAlpacaStaff, getUserIdFromToken } from "../utils/auth";

import "../components/SchoolAdmin.css";

function StaffAccessManager() {
  const staffMode = isAlpacaStaff();
  const currentUserId = getUserIdFromToken();

  const [teacherid, setTeacherid] = useState(null);
  const [name, setName] = useState("");
  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia("(orientation: portrait)").matches
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [searched, setSearched] = useState(false);

  const [staffList, setStaffList] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(true);

  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const [busyUserId, setBusyUserId] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    whoami().then((response) => {
      setTeacherid(response.data.stakeholder?.id);
      setName(response.data.stakeholder?.name || response.data.email);
    });
  }, []);

  useEffect(() => {
    const handleOrientationChange = () => {
      setIsPortrait(window.innerWidth < window.innerHeight);
    };
    window.addEventListener("resize", handleOrientationChange);
    return () => window.removeEventListener("resize", handleOrientationChange);
  }, []);

  const fetchStaffList = useCallback(async () => {
    setLoadingStaff(true);
    try {
      const data = await getAlpacaStaffList();
      setStaffList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching alpaca-staff list:", err);
      setError(`Failed to load current staff: ${err.message}`);
    } finally {
      setLoadingStaff(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const data = await getAlpacaStaffHistory();
      setHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching alpaca-staff history:", err);
      // Non-critical - don't block the rest of the page on this
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    if (!staffMode) return;
    fetchStaffList();
    fetchHistory();
  }, [staffMode, fetchStaffList, fetchHistory]);

  const handleSearch = async (event) => {
    event.preventDefault();
    if (searchTerm.trim().length < 3) {
      setSearchError("Enter at least 3 characters of the email address.");
      return;
    }
    setSearching(true);
    setSearchError(null);
    setSearched(true);
    try {
      const results = await searchUsersByEmail(searchTerm.trim());
      setSearchResults(Array.isArray(results) ? results : []);
    } catch (err) {
      console.error("Error searching users:", err);
      setSearchError(`Search failed: ${err.response?.data?.error?.message || err.message}`);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleGrant = async (user) => {
    if (!window.confirm(`Grant alpaca-staff access to ${user.email}? This gives them full, unrestricted access to every school in the system.`)) {
      return;
    }
    setError(null);
    setBusyUserId(user.id);
    try {
      await grantAlpacaStaff(user.id);
      setSuccessMessage(`Granted alpaca-staff access to ${user.email}.`);
      setSearchResults((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, roles: [...(u.roles || []), "alpaca-staff"] } : u))
      );
      await fetchStaffList();
      await fetchHistory();
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      console.error("Error granting alpaca-staff:", err);
      setError(err.response?.data?.error?.message || `Failed to grant access: ${err.message}`);
    } finally {
      setBusyUserId(null);
    }
  };

  const handleRevoke = async (user) => {
    if (user.id === currentUserId) return;
    if (!window.confirm(`Revoke alpaca-staff access from ${user.email}? They will be logged out and lose full-access permissions immediately.`)) {
      return;
    }
    setError(null);
    setBusyUserId(user.id);
    try {
      await revokeAlpacaStaff(user.id);
      setSuccessMessage(`Revoked alpaca-staff access from ${user.email}.`);
      setSearchResults((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, roles: (u.roles || []).filter((r) => r !== "alpaca-staff") } : u))
      );
      await fetchStaffList();
      await fetchHistory();
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      console.error("Error revoking alpaca-staff:", err);
      setError(err.response?.data?.error?.message || `Failed to revoke access: ${err.message}`);
    } finally {
      setBusyUserId(null);
    }
  };

  if (!staffMode) {
    return (
      <div>
        <Container fluid className="class-admin-container">
          <HeadingBanner name={name} isLoggedIn={true} cloud={true} teacherid={teacherid} />
          <Row className="class-admin-row">
            <LeftPanel />
            <Col sm={{ span: 10, offset: 2 }} className="class-admin-content">
              <Alert variant="danger" className="mt-4">
                This page is only available to alpaca-staff.
              </Alert>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div>
      <Container fluid className="class-admin-container">
        <HeadingBanner name={name} isLoggedIn={true} cloud={true} teacherid={teacherid} />

        <Row className="class-admin-row">
          <LeftPanel />
          <Col sm={{ span: 10, offset: 2 }} className="class-admin-content">

            <div className="class-admin-header">
              <h2 className="class-admin-page-title">Staff Access</h2>
              <p className="class-admin-page-subtitle">
                Grant or revoke alpaca-staff privileges. alpaca-staff bypasses all school
                restrictions and has full access to every school in the system - grant it
                deliberately.
              </p>
            </div>

            {successMessage && (
              <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)} className="mb-4">
                {successMessage}
              </Alert>
            )}

            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
                {error}
              </Alert>
            )}

            {/* Search + grant */}
            <Card className="mb-4">
              <Card.Body>
                <h5 className="mb-3">
                  <FaSearch className="me-2" />
                  Find a user
                </h5>
                <Form onSubmit={handleSearch} className="d-flex gap-2 mb-3">
                  <Form.Control
                    type="text"
                    placeholder="Search by email address..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Button type="submit" variant="primary" disabled={searching}>
                    {searching ? <Spinner animation="border" size="sm" /> : "Search"}
                  </Button>
                </Form>

                {searchError && <Alert variant="warning">{searchError}</Alert>}

                {searched && !searching && searchResults.length === 0 && !searchError && (
                  <Alert variant="info" className="mb-0">
                    No users found matching "{searchTerm}".
                  </Alert>
                )}

                {searchResults.length > 0 && (
                  <Table hover responsive size="sm">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Account type</th>
                        <th>Current roles</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchResults.map((user) => {
                        const hasStaff = (user.roles || []).includes("alpaca-staff");
                        const isSelf = user.id === currentUserId;
                        return (
                          <tr key={user.id}>
                            <td>{user.email}</td>
                            <td>{user.stakeholderType || "-"}</td>
                            <td>
                              {(user.roles || []).map((r) => (
                                <Badge bg={r === "alpaca-staff" ? "danger" : "secondary"} className="me-1" key={r}>
                                  {r}
                                </Badge>
                              ))}
                            </td>
                            <td className="text-end">
                              {hasStaff ? (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  disabled={isSelf || busyUserId === user.id}
                                  title={isSelf ? "You cannot revoke your own access" : ""}
                                  onClick={() => handleRevoke(user)}
                                >
                                  <FaUserMinus className="me-1" />
                                  Revoke
                                </Button>
                              ) : (
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  disabled={busyUserId === user.id}
                                  onClick={() => handleGrant(user)}
                                >
                                  <FaUserShield className="me-1" />
                                  Grant alpaca-staff
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>

            {/* Current staff list */}
            <Card className="mb-4">
              <Card.Body>
                <h5 className="mb-3">
                  <FaUserShield className="me-2" />
                  Current alpaca-staff ({staffList.length})
                </h5>
                {loadingStaff ? (
                  <div className="text-center py-3">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : staffList.length === 0 ? (
                  <Alert variant="warning" className="mb-0">
                    No alpaca-staff accounts found.
                  </Alert>
                ) : (
                  <Table hover responsive size="sm">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {staffList.map((user) => {
                        const isSelf = user.id === currentUserId;
                        return (
                          <tr key={user.id}>
                            <td>
                              {user.email}
                              {isSelf && <Badge bg="info" className="ms-2">You</Badge>}
                            </td>
                            <td className="text-end">
                              <Button
                                variant="outline-danger"
                                size="sm"
                                disabled={isSelf || busyUserId === user.id || staffList.length <= 1}
                                title={
                                  isSelf
                                    ? "You cannot revoke your own access"
                                    : staffList.length <= 1
                                    ? "Cannot revoke the last remaining alpaca-staff account"
                                    : ""
                                }
                                onClick={() => handleRevoke(user)}
                              >
                                <FaUserMinus className="me-1" />
                                Revoke
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>

            {/* Audit history */}
            <Card className="mb-4">
              <Card.Body>
                <h5 className="mb-3">
                  <FaHistory className="me-2" />
                  Recent changes
                </h5>
                {loadingHistory ? (
                  <div className="text-center py-3">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : history.length === 0 ? (
                  <p className="text-muted mb-0">No grant/revoke activity recorded yet.</p>
                ) : (
                  <Table hover responsive size="sm">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Action</th>
                        <th>User affected</th>
                        <th>Performed by</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((entry) => (
                        <tr key={entry.id}>
                          <td>{new Date(entry.created_at).toLocaleString()}</td>
                          <td>
                            <Badge bg={entry.action === "grant-alpaca-staff" ? "success" : "danger"}>
                              {entry.action === "grant-alpaca-staff" ? "Granted" : "Revoked"}
                            </Badge>
                          </td>
                          <td>{entry.metadata?.targetEmail || entry.elementid}</td>
                          <td>{entry.actor_name || entry.actor_userid}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>

          </Col>
        </Row>
      </Container>

      {isPortrait && <OrientationPrompt />}
    </div>
  );
}

export default StaffAccessManager;
